'use client';

import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Server,
  Lock,
  FileText,
  Cookie,
  ArrowRight,
  ScrollText,
  Tags,
} from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

export function LegalDirectory() {
  const { t } = useTranslation();

  const DOCUMENTS = [
    {
      id: 'gdpr',
      title: t.legal['GDPR Compliance'],
      description:
        t.legal[
          'How Open meets EU data protection requirements with enterprise-grade safeguards.'
        ],
      icon: ShieldCheck,
      href: '/gdpr',
      iconBg: 'bg-blue-500',
    },
    {
      id: 'sub-processes',
      title: t.legal['Subprocessors'],
      description:
        t.legal[
          'The third-party services we use to deliver Open, and how they handle your data.'
        ],
      icon: Server,
      href: '/sub-processes',
      iconBg: 'bg-purple-500',
    },
    {
      id: 'privacy-policy',
      title: t.legal['Privacy Policy'],
      description:
        t.legal['How we collect, use, and protect your personal information.'],
      icon: Lock,
      href: '/privacy-policy',
      iconBg: 'bg-emerald-500',
    },
    {
      id: 'terms',
      title: t.legal['Terms of Service'],
      description:
        t.legal['The terms and conditions for using the Open platform.'],
      icon: FileText,
      href: '/terms',
      iconBg: 'bg-orange-500',
    },
    {
      id: 'standard-terms',
      title: t.legal['Standard Terms'],
      description:
        t.legal[
          'The Cloud Service Standard Terms that apply to every Open agreement, with version history.'
        ],
      icon: ScrollText,
      href: '/legal/standard-terms',
      iconBg: 'bg-neutral-900',
    },
    {
      id: 'sla',
      title: t.legal['SLA Standard Terms'],
      description:
        t.legal[
          'Uptime, service credits and remedies that apply to every Open agreement, with version history.'
        ],
      icon: ScrollText,
      href: '/legal/sla',
      iconBg: 'bg-emerald-600',
    },
    {
      id: 'dpa-standard-terms',
      title: t.legal['DPA Standard Terms'],
      description:
        t.legal[
          'The Data Processing Agreement Standard Terms that apply to every Open DPA, with version history.'
        ],
      icon: ScrollText,
      href: '/legal/dpa-standard-terms',
      iconBg: 'bg-indigo-500',
    },
    {
      id: 'dpa-annexes',
      title: t.legal['DPA Annexes'],
      description:
        t.legal[
          'Processing details and technical and organizational security measures for every Open DPA.'
        ],
      icon: ShieldCheck,
      href: '/legal/dpa-annexes',
      iconBg: 'bg-teal-600',
    },
    {
      id: 'price-list',
      title: t.legal['Price List'],
      description:
        t.legal[
          'Every Open product and how it is charged, with upcoming changes.'
        ],
      icon: Tags,
      href: '/legal/price-list',
      iconBg: 'bg-rose-500',
    },
    {
      id: 'cookie-policy',
      title: t.legal['Cookie Policy'],
      description:
        t.legal[
          'The cookies and trackers we use, what they do, and how to control them.'
        ],
      icon: Cookie,
      href: '/cookie-policy',
      iconBg: 'bg-amber-500',
    },
    // /incentive-terms is a terms page and belongs in the terms directory. It
    // had no inbound link from anywhere on the site — live, indexable, and
    // reachable only through the sitemap.
    {
      id: 'incentive-terms',
      title: t.legal['Incentive Campaign Terms'],
      description:
        t.legal[
          'The rules for Open promotional campaigns, credits, and referral offers.'
        ],
      icon: FileText,
      href: '/incentive-terms',
      iconBg: 'bg-sky-500',
    },
  ];

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-medium text-white">
            <ShieldCheck className="size-4" />
            {t.legal['Legal & Compliance']}
          </div>
          <h1 className="mb-4 font-serif text-4xl md:text-5xl lg:text-6xl">
            {t.legal['Legal & Compliance']}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            {
              t.legal[
                'Everything about how we handle your data, privacy, and security. Transparent policies you can trust.'
              ]
            }
          </p>
        </motion.div>

        {/* Documents Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {DOCUMENTS.map((doc, index) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
            >
              <Link
                href={doc.href}
                className="group relative flex h-full flex-col rounded-2xl border-2 border-neutral-200 bg-white p-6 transition-all hover:border-black hover:shadow-lg"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div
                    className={`flex size-12 items-center justify-center rounded-xl ${doc.iconBg} text-white`}
                  >
                    <doc.icon className="size-6" />
                  </div>
                  <ArrowRight className="size-5 text-neutral-300 transition-all group-hover:translate-x-1 group-hover:text-black" />
                </div>

                <h2 className="mb-2 text-xl">{doc.title}</h2>
                <p className="flex-1 text-sm text-neutral-600">
                  {doc.description}
                </p>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
